import { useRef, useState } from 'react';

import type { RedemptionModalOrderData, Survey } from '@shared/dream-components';
import { FreeProductCheckoutModalElement } from '@shared/dream-components/src';

import ActionForm from '~/components/ActionForm';
import { useComponentFetcher } from '~/components/ComponentErrorBoundary/useComponentFetcher';
import type useGDPRTranslations from '~/components/DreamBuilder/nodes/hooks/useGDPRTranslations';
import { usePageContext } from '~/context/usePageContext';
import useBoostAffiliate, { BOOST_AFFILIATE_COOKIE_NAME } from '~/hooks/useBoostAffiliate';
import type { ToastData } from '~/hooks/useToast';
import useToast from '~/hooks/useToast';
import type { Product } from '~/interfaces/product';
import type { RootLoaderData } from '~/root';
import { getNectarVisitToken, getOrCreateAnonymousUserId } from '~/utils/nectarTracking';
import { NECTAR_SITE_ID_FIELD } from '~/utils/nectarTrackingFields';

import type useFreeProductCheckoutModalTranslations from '../_hooks/useFreeProductCheckoutModalTranslations';
import { MODAL_ELEMENT_ATTRS } from './constants';

export interface LoggedInCheckoutModalProps {
  product: Product;
  productVariantId: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (data: { order?: RedemptionModalOrderData; toast?: ToastData; redemptionToken?: string }) => void;
  site: RootLoaderData['site'];
  gdprTranslations: ReturnType<typeof useGDPRTranslations>;
  translations: ReturnType<typeof useFreeProductCheckoutModalTranslations>;
  form?: Survey;
}

const LoggedInCheckoutModal = ({
  product,
  productVariantId,
  isOpen,
  onClose,
  onSuccess,
  site,
  gdprTranslations,
  translations,
  form,
}: LoggedInCheckoutModalProps) => {
  const fetcher = useComponentFetcher();
  const formRef = useRef<HTMLFormElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { cookies, visitToken, referralId } = usePageContext();
  const boostAffiliateRecommendingPublicationId = useBoostAffiliate(cookies[BOOST_AFFILIATE_COOKIE_NAME] || undefined);

  const fetcherData = fetcher.data as
    | { toast?: ToastData; order?: RedemptionModalOrderData; redemptionToken?: string }
    | undefined;
  useToast(fetcherData?.toast);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);

    const formData = new FormData(e.currentTarget);
    if (typeof window !== 'undefined') {
      formData.set('purchase_url', window.location.href);
      formData.set('anonymous_user_id', getOrCreateAnonymousUserId());
      formData.set('nectar_visit_token', getNectarVisitToken());
      if (site.id) formData.set(NECTAR_SITE_ID_FIELD, site.id);
    }
    fetcher.submit(formData, {
      method: 'post',
      action: `/products/${product.slug}`,
    });
  };

  const handleSuccess = (data: { order?: RedemptionModalOrderData; toast?: ToastData; redemptionToken?: string }) => {
    setIsLoading(false);
    formRef.current?.reset();
    onSuccess(data);
  };

  const handleError = () => {
    setIsLoading(false);
  };

  return (
    <ActionForm
      id="free-product-logged-in-checkout-modal"
      fetcher={fetcher}
      formRef={formRef}
      method="post"
      action={`/products/${product.slug}`}
      onSubmit={handleSubmit}
      onSuccess={handleSuccess}
      onError={handleError}
    >
      <input type="hidden" name="product_variant_id" value={productVariantId} />
      {form && <input type="hidden" name="form_id" value={form.id} />}
      <input type="hidden" name="sent_from_orchid" value="true" />
      {referralId && <input type="hidden" name="ref" value={referralId} />}
      {boostAffiliateRecommendingPublicationId && (
        <input type="hidden" name="bhba" value={boostAffiliateRecommendingPublicationId} />
      )}
      {visitToken && <input type="hidden" name="visit_token" value={visitToken} />}
      <input type="hidden" name="is_js_enabled" value="true" />
      <FreeProductCheckoutModalElement
        element={{
          type: 'freeProductCheckoutModal',
          attrs: MODAL_ELEMENT_ATTRS,
          content: [{ id: '', text: '' }],
        }}
        isOpen={isOpen}
        onOpenChange={onClose}
        isLoading={isLoading}
        translations={translations}
        gdprTranslations={gdprTranslations}
        hasTermsOfService={site.viewable_site_version.settings.terms_of_service_enabled}
        hasCustomTermsOfService={site.viewable_site_version.settings.terms_of_service}
        customTermsOfServiceText={site.viewable_site_version.settings.terms_of_service_text || undefined}
        defaultTOSConsentValue={false}
        onEmailChange={() => {}}
        isPreviewing={false}
        form={form}
        hideEmailInput={true}
      >
        {null}
      </FreeProductCheckoutModalElement>
    </ActionForm>
  );
};

export default LoggedInCheckoutModal;
